/**
 * Shared grouping + search logic for permission lists. Used by both the
 * editable PermissionsMultiSelectDialog and the read-only
 * PermissionsReferenceDialog so the two stay visually consistent.
 */

/** Minimal shape a permission needs to be grouped and searched. */
export interface GroupablePermission {
  code: string;
  name: string;
  description?: string | null;
  category: string;
}

export interface PermissionGroup<T> {
  category: string;
  permissions: T[];
}

/**
 * Group permissions by category (categories sorted alphabetically, permissions
 * sorted by code within each), keeping only rows that match `search` across
 * code, name, and description. An empty search keeps everything.
 */
export function groupPermissionsByCategory<T extends GroupablePermission>(
  permissions: readonly T[],
  search: string
): PermissionGroup<T>[] {
  const query = search.trim().toLowerCase();
  const matches = (p: T) => {
    if (!query) return true;
    return (
      p.code.toLowerCase().includes(query) ||
      p.name.toLowerCase().includes(query) ||
      (p.description ?? "").toLowerCase().includes(query)
    );
  };

  const byCategory = new Map<string, T[]>();
  permissions.filter(matches).forEach((p) => {
    const list = byCategory.get(p.category) ?? [];
    list.push(p);
    byCategory.set(p.category, list);
  });

  return [...byCategory.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([category, perms]) => ({
      category,
      permissions: [...perms].sort((a, b) => a.code.localeCompare(b.code)),
    }));
}
